import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { api } from '../lib/api';
import { haptic } from '../lib/telegram';
import type { ArtistProfile, Service, User } from '../types';
import { Button, CopyLink, ErrorNotice, Field, Input } from '../components/ui';
import { ImageField } from '../components/image-field';

/** Валюты перечисляются как удобно: `RUB, USD` или `RUB/USD`. Первая — основная. */
const CURRENCY_SEPARATORS = /[\s,;/|]+/;

function parseCurrencies(value: string): string[] {
  return [
    ...new Set(
      value
        .toUpperCase()
        .split(CURRENCY_SEPARATORS)
        .map((code) => code.trim())
        .filter(Boolean),
    ),
  ];
}

/** Проверка совпадает с `timezoneSchema` на сервере: регион или сдвиг вида +7. */
const UTC_OFFSET_PATTERN = /^(?:UTC|GMT)?[+-]\d{1,2}(?::?00)?$/i;

function isKnownTimezone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('ru-RU', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

const profileSchema = z.object({
  displayName: z.string().trim().min(1, 'Укажите имя').max(128),
  description: z.string().trim().max(2000),
  currencies: z
    .string()
    .trim()
    .refine((value) => {
      const codes = parseCurrencies(value);
      return (
        codes.length > 0 && codes.length <= 6 && codes.every((code) => /^[A-Z]{3}$/.test(code))
      );
    }, 'Например: RUB, USD — по три латинские буквы'),
  timezone: z
    .string()
    .trim()
    .refine(
      (value) => UTC_OFFSET_PATTERN.test(value) || isKnownTimezone(value),
      'Регион вида Europe/Moscow или сдвиг вида +7',
    ),
  termsText: z.string().trim().max(5000),
});

const serviceSchema = z.object({
  title: z.string().trim().min(1, 'Укажите название').max(150),
  description: z.string().trim().max(3000),
  basePrice: z.string().regex(/^\d+([.,]\d{1,2})?$/, 'Укажите сумму'),
  durationMinDays: z.number().int().min(1).max(365),
  durationMaxDays: z.number().int().min(1).max(365),
});

type ProfileForm = z.infer<typeof profileSchema>;
type ServiceForm = z.infer<typeof serviceSchema>;

/** Русские названия полей: серверная ошибка называет поле, а не только запрос. */
const ONBOARDING_PROFILE_LABELS: Record<string, string> = {
  displayName: 'Имя',
  description: 'О себе',
  currencies: 'Валюты',
  timezone: 'Часовой пояс',
  termsText: 'Правила работы',
};

const ONBOARDING_SERVICE_LABELS: Record<string, string> = {
  title: 'Название',
  description: 'Описание',
  basePrice: 'Цена от',
  durationMinDays: 'Срок от, дней',
  durationMaxDays: 'Срок до, дней',
  previewImageId: 'Пример работы',
  secondaryImageId: 'Второй пример',
};

export function OnboardingPage({
  user,
  onComplete,
}: {
  user: User;
  onComplete: (profile: ArtistProfile) => void;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [profile, setProfile] = useState<ArtistProfile | null>(null);
  // Услуга не обязательна для приёма заявок: без неё клиент просто описывает работу сам.
  const [skippedService, setSkippedService] = useState(false);
  // Примеры работ живут вне формы: загрузка отдаёт id картинки, а не значение поля.
  const [previewImageId, setPreviewImageId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [secondaryImageId, setSecondaryImageId] = useState<string | null>(null);
  const [secondaryUrl, setSecondaryUrl] = useState<string | null>(null);
  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      displayName: [user.firstName, user.lastName].filter(Boolean).join(' '),
      description: '',
      currencies: 'RUB',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow',
      termsText: '',
    },
  });
  const serviceForm = useForm<ServiceForm>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      title: 'Портрет',
      description: '',
      basePrice: '3000',
      durationMinDays: 7,
      durationMaxDays: 14,
    },
  });

  const createProfile = useMutation({
    mutationFn: (values: ProfileForm) =>
      api<ArtistProfile>('/artist/profile', {
        method: 'POST',
        body: {
          ...values,
          currencies: parseCurrencies(values.currencies),
          description: values.description || null,
          termsText: values.termsText || null,
        },
      }),
    onSuccess: (created) => {
      setProfile(created);
      setStep(2);
      haptic('success');
    },
  });
  const createService = useMutation({
    mutationFn: (values: ServiceForm) =>
      api<Service>('/services', {
        method: 'POST',
        body: {
          ...values,
          basePrice: values.basePrice.replace(',', '.'),
          currency: profile?.currency ?? 'RUB',
          previewImageId,
          secondaryImageId,
        },
      }),
    onSuccess: () => {
      setStep(3);
      haptic('success');
    },
  });

  return (
    <main className="onboarding">
      <div className="onboarding__art">
        <div className="brand-mark">AF</div>
        <div className="onboarding__stroke onboarding__stroke--one" />
        <div className="onboarding__stroke onboarding__stroke--two" />
      </div>
      <div className="onboarding__content">
        <div className="stepper">
          {[1, 2, 3].map((number) => (
            <span key={number} className={number <= step ? 'is-active' : ''} />
          ))}
        </div>

        {step === 1 && (
          <>
            <p className="eyebrow">Шаг 1 из 3</p>
            <h1>Расскажите о себе</h1>
            <p className="muted">Эти данные увидят клиенты на публичной странице.</p>
            <form
              className="stack"
              onSubmit={profileForm.handleSubmit((values) => createProfile.mutate(values))}
            >
              <Field label="Имя" error={profileForm.formState.errors.displayName?.message}>
                <Input {...profileForm.register('displayName')} />
              </Field>
              <Field label="О себе" error={profileForm.formState.errors.description?.message}>
                <textarea
                  className="input input--textarea"
                  placeholder="Что вы рисуете и в каком стиле"
                  {...profileForm.register('description')}
                />
              </Field>
              <div className="form-grid">
                <Field
                  label="Валюты"
                  hint="Через запятую или слэш. Первая — основная"
                  error={profileForm.formState.errors.currencies?.message}
                >
                  <Input placeholder="RUB, USD" {...profileForm.register('currencies')} />
                </Field>
                <Field
                  label="Часовой пояс"
                  hint="Регион или сдвиг: +7"
                  error={profileForm.formState.errors.timezone?.message}
                >
                  <Input placeholder="Europe/Moscow" {...profileForm.register('timezone')} />
                </Field>
              </div>
              <Field label="Правила работы" error={profileForm.formState.errors.termsText?.message}>
                <textarea
                  className="input input--textarea"
                  placeholder="Предоплата, сроки, правки"
                  {...profileForm.register('termsText')}
                />
              </Field>
              {createProfile.error && (
                <ErrorNotice error={createProfile.error} labels={ONBOARDING_PROFILE_LABELS} />
              )}
              <Button type="submit" wide disabled={createProfile.isPending}>
                {createProfile.isPending ? 'Сохраняем…' : 'Продолжить'}
              </Button>
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <p className="eyebrow">Шаг 2 из 3</p>
            <h1>Первая услуга</h1>
            <p className="muted">
              Клиент сможет выбрать её при отправке заявки. Шаг необязательный — заявки принимаются
              и без услуги.
            </p>
            <form
              className="stack"
              onSubmit={serviceForm.handleSubmit((values) => createService.mutate(values))}
            >
              <Field label="Название" error={serviceForm.formState.errors.title?.message}>
                <Input {...serviceForm.register('title')} />
              </Field>
              <Field label="Описание" error={serviceForm.formState.errors.description?.message}>
                <textarea
                  className="input input--textarea"
                  placeholder="Что входит в работу"
                  {...serviceForm.register('description')}
                />
              </Field>
              <ImageField
                label="Пример работы"
                hint="Обложка услуги — видна в поиске"
                kind="cover"
                url={previewUrl}
                disabled={createService.isPending}
                onChange={(imageId) => {
                  setPreviewImageId(imageId);
                  setPreviewUrl(imageId ? `/v1/images/${imageId}` : null);
                }}
              />
              {/* Второй пример: больше двух услуга не показывает — карточка должна читаться. */}
              <ImageField
                label="Второй пример"
                hint="Необязательно. Второй кадр встанет рядом с обложкой"
                kind="cover"
                url={secondaryUrl}
                disabled={createService.isPending}
                onChange={(imageId) => {
                  setSecondaryImageId(imageId);
                  setSecondaryUrl(imageId ? `/v1/images/${imageId}` : null);
                }}
              />
              <Field label="Цена от" error={serviceForm.formState.errors.basePrice?.message}>
                <Input inputMode="decimal" {...serviceForm.register('basePrice')} />
              </Field>
              <div className="form-grid">
                <Field label="Срок от, дней">
                  <Input
                    type="number"
                    {...serviceForm.register('durationMinDays', { valueAsNumber: true })}
                  />
                </Field>
                <Field label="Срок до, дней">
                  <Input
                    type="number"
                    {...serviceForm.register('durationMaxDays', { valueAsNumber: true })}
                  />
                </Field>
              </div>
              {createService.error && (
                <ErrorNotice error={createService.error} labels={ONBOARDING_SERVICE_LABELS} />
              )}
              <Button type="submit" wide disabled={createService.isPending}>
                {createService.isPending ? 'Создаём…' : 'Опубликовать услугу'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                wide
                disabled={createService.isPending}
                onClick={() => {
                  setSkippedService(true);
                  setStep(3);
                }}
              >
                Пропустить — добавлю позже
              </Button>
            </form>
          </>
        )}

        {step === 3 && profile && (
          <div className="success-panel">
            <div className="success-panel__icon">✓</div>
            <p className="eyebrow">Готово</p>
            <h1>Рабочая область создана</h1>
            <p className="muted">
              {skippedService
                ? 'Заявки уже можно принимать: клиент опишет работу сам, а услугу вы добавите в профиле.'
                : 'Поделитесь ссылкой и ожидайте первую заявку.'}
            </p>
            <CopyLink
              url={profile.publicLink.url ?? `${location.origin}${profile.publicLink.path}`}
            />
            <Button wide onClick={() => onComplete(profile)}>
              Открыть Art Flow
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
